<template>
  <div fluid class="pa-4">
    <v-card elevation="3" class="mx-auto">
      <!-- Header -->
      <v-card-title
        class="white--text py-4"
        :style="{ 'background-color': getThemeColor }"
      >
        <v-icon left color="white" large>mdi-clock-time-four-outline</v-icon>
        <span class="text-h5 font-weight-bold">Time Billing System</span>
        <v-spacer></v-spacer>
        <v-chip color="white" text-color="primary" class="font-weight-bold">
          {{ currentDate }}
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <v-row>
          <!-- Left Column - Client & Service Selection -->
          <v-col cols="12" md="5">
            <v-card outlined class="mb-4">
              <v-card-subtitle class="pb-0 font-weight-bold text-subtitle-1">
                Client / Patient Selection
              </v-card-subtitle>
              <v-card-text>
                <v-autocomplete
                  v-model="selectedClient"
                  :items="clients"
                  item-text="name"
                  item-value="id"
                  label="Select Client/Patient"
                  prepend-icon="mdi-account-search"
                  outlined
                  dense
                  clearable
                  @change="onClientSelected"
                  :disabled="isTimerRunning"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar color="primary">
                      <span class="white--text">{{ item.initials }}</span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle
                        >{{ item.type }} -
                        {{ item.phone }}</v-list-item-subtitle
                      >
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <!-- Invoice Selection -->
                <v-select
                  v-if="selectedClient"
                  v-model="selectedInvoice"
                  :items="invoiceOptions"
                  item-text="label"
                  item-value="id"
                  label="Select Invoice"
                  prepend-icon="mdi-file-document-outline"
                  outlined
                  dense
                  :disabled="isTimerRunning"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.id !== 'new'">
                        Balance: {{ getCurrency }}{{ item.balance }} | Due:
                        {{ item.dueDate }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="item.id !== 'new'">
                      <v-chip
                        small
                        :color="item.status === 'Paid' ? 'success' : 'warning'"
                      >
                        {{ item.status }}
                      </v-chip>
                    </v-list-item-action>
                  </template>
                </v-select>

                <!-- Service Selection -->
                <v-select
                  v-model="selectedService"
                  :items="services"
                  item-text="name"
                  item-value="id"
                  label="Select Service"
                  prepend-icon="mdi-briefcase-outline"
                  outlined
                  dense
                  :disabled="isTimerRunning"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.rateType }} - {{ getCurrency
                        }}{{ item.rate }}/{{ item.unit }}
                        <v-chip x-small v-if="item.category" class="ml-2">{{
                          item.category
                        }}</v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-select>

                <!-- Billing Rate Type -->
                <v-select
                  v-model="rateType"
                  :items="rateTypes"
                  label="Rate Type"
                  prepend-icon="mdi-currency-usd"
                  outlined
                  dense
                  :disabled="isTimerRunning"
                ></v-select>

                <!-- Time Unit Selection -->
                <v-select
                  v-model="timeUnit"
                  :items="timeUnits"
                  label="Billing Increment"
                  prepend-icon="mdi-clock-outline"
                  outlined
                  dense
                  hint="Time will be rounded up to nearest increment"
                  persistent-hint
                  :disabled="isTimerRunning"
                ></v-select>
              </v-card-text>
            </v-card>

            <!-- Timer Controls -->
            <v-card outlined color="grey lighten-5">
              <v-card-text class="text-center pa-6">
                <!-- Timer Display -->
                <div
                  class="display-2 font-weight-bold mb-4"
                  :class="isTimerRunning ? 'primary--text' : 'grey--text'"
                >
                  {{ formattedTime }}
                </div>

                <!-- Timer Status -->
                <v-chip
                  :color="timerStatusColor"
                  text-color="white"
                  class="mb-4 px-6"
                  large
                >
                  <v-icon left>{{ timerStatusIcon }}</v-icon>
                  {{ timerStatus }}
                </v-chip>

                <!-- Timer Buttons -->
                <div class="d-flex justify-center gap-3 mb-4">
                  <v-btn
                    large
                    :color="isTimerRunning ? 'warning' : 'success'"
                    :disabled="!canStartTimer"
                    @click="toggleTimer"
                    class="px-8"
                  >
                    <v-icon left>{{
                      isTimerRunning ? "mdi-pause" : "mdi-play"
                    }}</v-icon>
                    {{ isTimerRunning ? "Pause" : "Start" }}
                  </v-btn>

                  <v-btn
                    large
                    color="error"
                    :disabled="!isTimerRunning && elapsedSeconds === 0"
                    @click="stopTimer"
                    class="px-8"
                  >
                    <v-icon left>mdi-stop</v-icon>
                    Stop & Bill
                  </v-btn>
                </div>

                <!-- Manual Time Entry -->
                <v-expansion-panels flat class="mt-4">
                  <v-expansion-panel>
                    <v-expansion-panel-header class="px-0">
                      <span class="text-subtitle-2">
                        <v-icon small left>mdi-clock-edit-outline</v-icon>
                        Manual Time Entry
                      </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12">
                          <v-menu
                            v-model="dateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="manualDate"
                                label="Date"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                outlined
                                dense
                                :disabled="isTimerRunning"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="manualDate"
                              @input="dateMenu = false"
                            ></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="manualStartTime"
                            label="Start Time"
                            type="time"
                            prepend-icon="mdi-clock-start"
                            outlined
                            dense
                            :disabled="isTimerRunning"
                            @input="calculateManualDuration"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="manualEndTime"
                            label="End Time"
                            type="time"
                            prepend-icon="mdi-clock-end"
                            outlined
                            dense
                            :disabled="isTimerRunning"
                            @input="calculateManualDuration"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="manualDuration"
                            label="Duration (minutes)"
                            type="number"
                            prepend-icon="mdi-timer-outline"
                            outlined
                            dense
                            readonly
                            hint="Auto-calculated from start and end time"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-btn
                            block
                            color="primary"
                            :disabled="!canAddManualTime"
                            @click="addManualTime"
                          >
                            <v-icon left>mdi-plus</v-icon>
                            Add Manual Time
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column - Current Session & History -->
          <v-col cols="12" md="7">
            <!-- Current Session Details -->
            <v-card
              outlined
              class="mb-4"
              v-if="selectedClient && selectedService"
            >
              <v-card-subtitle class="pb-0 font-weight-bold text-subtitle-1">
                Current Session Details
              </v-card-subtitle>
              <v-card-text>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold">Client:</td>
                      <td>{{ getClientName }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Service:</td>
                      <td>{{ getServiceName }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Rate:</td>
                      <td>
                        {{ getCurrency }}{{ getCurrentRate }}/{{
                          getCurrentUnit
                        }}
                        ({{ rateType }})
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Elapsed Time:</td>
                      <td>{{ formattedElapsedTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Billable Time:</td>
                      <td class="success--text font-weight-bold">
                        {{ billableTime }} {{ getCurrentUnit }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Estimated Cost:</td>
                      <td class="primary--text text-h6 font-weight-bold">
                        {{ getCurrency }}{{ estimatedCost }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <!-- Notes -->
                <v-textarea
                  v-model="sessionNotes"
                  label="Session Notes"
                  outlined
                  rows="3"
                  dense
                  class="mt-4"
                  hint="Add notes about this billing session"
                  persistent-hint
                ></v-textarea>
              </v-card-text>
            </v-card>

            <!-- Billing History -->
            <v-card outlined>
              <v-card-subtitle class="pb-0 d-flex align-center">
                <span class="font-weight-bold text-subtitle-1"
                  >Billing History</span
                >
                <v-spacer></v-spacer>
                <v-chip small>{{ billingHistory.length }} entries</v-chip>
              </v-card-subtitle>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="historyHeaders"
                  :items="billingHistory"
                  :items-per-page="5"
                  class="elevation-0"
                  dense
                >
                  <template v-slot:item.client="{ item }">
                    <div class="d-flex align-center py-2">
                      <v-avatar size="32" color="primary" class="mr-2">
                        <span class="white--text text-caption">{{
                          item.clientInitials
                        }}</span>
                      </v-avatar>
                      <span>{{ item.client }}</span>
                    </div>
                  </template>

                  <template v-slot:item.duration="{ item }">
                    <v-chip small outlined>
                      {{ item.duration }}
                    </v-chip>
                  </template>

                  <template v-slot:item.amount="{ item }">
                    <span class="font-weight-bold"
                      >{{ getCurrency }}{{ item.amount }}</span
                    >
                  </template>

                  <template v-slot:item.invoice="{ item }">
                    <v-chip
                      small
                      :color="
                        item.invoiceStatus === 'Paid' ? 'success' : 'warning'
                      "
                      text-color="white"
                    >
                      {{ item.invoice }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn icon small @click="viewBillingDetail(item)">
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="deleteBilling(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      bottom
      right
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Billing</v-card-title>
        <v-card-text>
          <div class="text-body-1 mb-4">
            Are you sure you want to stop the timer and create this billing
            entry?
          </div>
          <v-simple-table dense v-if="confirmDialogData">
            <tbody>
              <tr>
                <td class="font-weight-bold">Client:</td>
                <td>{{ confirmDialogData.client }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Service:</td>
                <td>{{ confirmDialogData.service }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Duration:</td>
                <td>{{ confirmDialogData.duration }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Amount:</td>
                <td class="primary--text text-h6">
                  {{ getCurrency }}{{ confirmDialogData.amount }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmBilling">Confirm & Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "TimeBillingComponent",

  data() {
    return {
      // Timer state
      isTimerRunning: false,
      elapsedSeconds: 0,
      timerInterval: null,
      startTime: null,
      pausedTime: 0,

      // Form data
      selectedClient: null,
      selectedInvoice: null,
      selectedService: null,
      rateType: "Standard",
      timeUnit: "6 minutes (0.1 hours)",
      sessionNotes: "",

      // Manual time entry
      dateMenu: false,
      manualDate: new Date().toISOString().substr(0, 10),
      manualStartTime: "",
      manualEndTime: "",
      manualDuration: 0,

      // Data arrays
      clients: [
        {
          id: 1,
          name: "John Smith",
          type: "Client",
          phone: "555-0101",
          initials: "JS",
        },
        {
          id: 2,
          name: "Sarah Johnson",
          type: "Patient",
          phone: "555-0102",
          initials: "SJ",
        },
        {
          id: 3,
          name: "Michael Brown",
          type: "Client",
          phone: "555-0103",
          initials: "MB",
        },
        {
          id: 4,
          name: "Emily Davis",
          type: "Patient",
          phone: "555-0104",
          initials: "ED",
        },
        {
          id: 5,
          name: "Robert Wilson",
          type: "Client",
          phone: "555-0105",
          initials: "RW",
        },
      ],

      clientInvoices: {},

      services: [
        {
          id: 1,
          name: "Legal Consultation",
          rate: 350,
          rateType: "Hourly",
          unit: "hour",
          category: "Legal",
        },
        {
          id: 2,
          name: "Court Appearance",
          rate: 500,
          rateType: "Hourly",
          unit: "hour",
          category: "Legal",
        },
        {
          id: 3,
          name: "Document Review",
          rate: 250,
          rateType: "Hourly",
          unit: "hour",
          category: "Legal",
        },
        {
          id: 4,
          name: "Medical Consultation",
          rate: 200,
          rateType: "Hourly",
          unit: "hour",
          category: "Medical",
        },
        {
          id: 5,
          name: "Emergency Visit",
          rate: 400,
          rateType: "Hourly",
          unit: "hour",
          category: "Medical",
        },
        {
          id: 6,
          name: "Follow-up Visit",
          rate: 150,
          rateType: "Fixed",
          unit: "visit",
          category: "Medical",
        },
        {
          id: 7,
          name: "Dental Cleaning",
          rate: 120,
          rateType: "Fixed",
          unit: "procedure",
          category: "Dental",
        },
        {
          id: 8,
          name: "Tax Preparation",
          rate: 175,
          rateType: "Hourly",
          unit: "hour",
          category: "Accounting",
        },
        {
          id: 9,
          name: "Pro Bono Service",
          rate: 0,
          rateType: "Pro Bono",
          unit: "hour",
          category: "Legal",
        },
      ],

      rateTypes: [
        "Standard",
        "After-hours",
        "Emergency",
        "Weekend",
        "Pro Bono",
      ],

      timeUnits: [
        "6 minutes (0.1 hours)",
        "10 minutes (0.17 hours)",
        "15 minutes (0.25 hours)",
        "30 minutes (0.5 hours)",
        "1 hour",
      ],

      billingHistory: [],

      historyHeaders: [
        { text: "Date", value: "date", sortable: true },
        { text: "Client", value: "client", sortable: true },
        { text: "Service", value: "service", sortable: true },
        { text: "Duration", value: "duration", sortable: false },
        { text: "Amount", value: "amount", sortable: true },
        { text: "Invoice", value: "invoice", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],

      // UI state
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      confirmDialog: false,
      confirmDialogData: null,
    };
  },

  computed: {
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formattedTime() {
      const hours = Math.floor(this.elapsedSeconds / 3600);
      const minutes = Math.floor((this.elapsedSeconds % 3600) / 60);
      const seconds = this.elapsedSeconds % 60;

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}:${String(seconds).padStart(2, "0")}`;
    },

    formattedElapsedTime() {
      const hours = Math.floor(this.elapsedSeconds / 3600);
      const minutes = Math.floor((this.elapsedSeconds % 3600) / 60);

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m ${this.elapsedSeconds % 60}s`;
    },

    timerStatus() {
      if (this.isTimerRunning) return "Running";
      if (this.elapsedSeconds > 0) return "Paused";
      return "Ready";
    },

    timerStatusColor() {
      if (this.isTimerRunning) return "success";
      if (this.elapsedSeconds > 0) return "warning";
      return "grey";
    },

    timerStatusIcon() {
      if (this.isTimerRunning) return "mdi-play-circle";
      if (this.elapsedSeconds > 0) return "mdi-pause-circle";
      return "mdi-clock-outline";
    },

    canStartTimer() {
      return (
        this.selectedClient && this.selectedService && this.selectedInvoice
      );
    },

    canAddManualTime() {
      return (
        this.selectedClient &&
        this.selectedService &&
        this.selectedInvoice &&
        this.manualDate &&
        this.manualStartTime &&
        this.manualEndTime &&
        this.manualDuration > 0 &&
        !this.isTimerRunning
      );
    },

    invoiceOptions() {
      if (!this.selectedClient) return [];

      const clientId = this.selectedClient;
      const invoices = this.clientInvoices[clientId] || [];

      return [{ id: "new", label: "+ Create New Invoice" }, ...invoices];
    },

    getClientName() {
      if (!this.selectedClient) return "";
      const client = this.clients.find((c) => c.id === this.selectedClient);
      return client ? client.name : "";
    },

    getServiceName() {
      if (!this.selectedService) return "";
      const service = this.services.find((s) => s.id === this.selectedService);
      return service ? service.name : "";
    },

    getCurrentRate() {
      if (!this.selectedService) return 0;
      const service = this.services.find((s) => s.id === this.selectedService);
      if (!service) return 0;

      let rate = service.rate;

      // Apply rate multipliers
      if (this.rateType === "After-hours") rate = rate * 1.5;
      if (this.rateType === "Emergency") rate = rate * 2;
      if (this.rateType === "Weekend") rate = rate * 1.25;
      if (this.rateType === "Pro Bono") rate = 0;

      return rate.toFixed(2);
    },

    getCurrentUnit() {
      if (!this.selectedService) return "hour";
      const service = this.services.find((s) => s.id === this.selectedService);
      return service ? service.unit : "hour";
    },

    billableTime() {
      const minutes = this.elapsedSeconds / 60;
      const increment = this.getTimeIncrement();

      // Round up to nearest increment
      const billableMinutes = Math.ceil(minutes / increment) * increment;

      if (this.getCurrentUnit === "hour") {
        return (billableMinutes / 60).toFixed(2);
      }
      return billableMinutes.toFixed(0);
    },

    estimatedCost() {
      const rate = parseFloat(this.getCurrentRate);
      const time = parseFloat(this.billableTime);

      if (this.getCurrentUnit === "hour") {
        return (rate * time).toFixed(2);
      }
      return rate.toFixed(2); // Fixed rate services
    },
  },

  methods: {
    toggleTimer() {
      if (this.isTimerRunning) {
        this.pauseTimer();
      } else {
        this.startTimer();
      }
    },

    startTimer() {
      this.isTimerRunning = true;
      this.startTime = Date.now() - this.pausedTime * 1000;

      this.timerInterval = setInterval(() => {
        this.elapsedSeconds = Math.floor((Date.now() - this.startTime) / 1000);
      }, 1000);

      this.showSnackbar("Timer started", "success");
    },

    pauseTimer() {
      this.isTimerRunning = false;
      this.pausedTime = this.elapsedSeconds;
      clearInterval(this.timerInterval);
      this.showSnackbar("Timer paused", "warning");
    },

    stopTimer() {
      if (
        !this.selectedClient ||
        !this.selectedService ||
        !this.selectedInvoice
      ) {
        this.showSnackbar(
          "Please select client, service, and invoice",
          "error"
        );
        return;
      }

      this.confirmDialogData = {
        client: this.getClientName,
        service: this.getServiceName,
        duration: this.formattedElapsedTime,
        amount: this.estimatedCost,
      };

      this.confirmDialog = true;
    },

    confirmBilling() {
      this.isTimerRunning = false;
      clearInterval(this.timerInterval);

      this.saveBilling();

      // Reset timer
      this.elapsedSeconds = 0;
      this.pausedTime = 0;
      this.startTime = null;
      this.sessionNotes = "";

      this.confirmDialog = false;
      this.showSnackbar("Billing saved successfully!", "success");
    },

    async saveBilling() {
      const client = this.clients.find((c) => c.id === this.selectedClient);
      const service = this.services.find((s) => s.id === this.selectedService);
      const invoice = this.invoiceOptions.find(
        (i) => i.id === this.selectedInvoice
      );

      const billingEntry = {
        id: Date.now(),
        date: new Date().toLocaleDateString(),
        client: client.name,
        clientInitials: client.initials,
        service: service.name,
        duration: this.formattedElapsedTime,
        billableTime: this.billableTime,
        rate: this.getCurrentRate,
        rateType: this.rateType,
        amount: this.estimatedCost,
        invoice: invoice.id === "new" ? "INV-" + Date.now() : invoice.label,
        invoiceStatus: "Pending",
        notes: this.sessionNotes,
        timestamp: new Date().toISOString(),
      };

      this.billingHistory.unshift(billingEntry);

      // Simulate API call
      try {
        const response = await this.simulateApiCall("/auth/save-billing", {
          method: "POST",
          body: billingEntry,
        });
        console.log("Billing saved:", response);
      } catch (error) {
        console.error("Error saving billing:", error);
      }
    },

    getTimeIncrement() {
      const unit = this.timeUnit;
      if (unit.includes("6 minutes")) return 6;
      if (unit.includes("10 minutes")) return 10;
      if (unit.includes("15 minutes")) return 15;
      if (unit.includes("30 minutes")) return 30;
      if (unit.includes("1 hour")) return 60;
      return 6; // default
    },

    onClientSelected() {
      if (!this.selectedClient) {
        this.selectedInvoice = null;
        return;
      }

      this.loadClientInvoices(this.selectedClient);
    },

    async loadClientInvoices(clientId) {
      // Simulate API call to load invoices
      const mockInvoices = [
        {
          id: "INV-001",
          label: "INV-001 - Dec 2024",
          balance: 1250.0,
          dueDate: "12/31/2024",
          status: "Pending",
        },
        {
          id: "INV-002",
          label: "INV-002 - Nov 2024",
          balance: 0,
          dueDate: "11/30/2024",
          status: "Paid",
        },
      ];

      this.clientInvoices = {
        ...this.clientInvoices,
        [clientId]: mockInvoices,
      };

      // Auto-select the first pending invoice or new invoice
      const pendingInvoice = mockInvoices.find(
        (inv) => inv.status === "Pending"
      );
      this.selectedInvoice = pendingInvoice ? pendingInvoice.id : "new";

      try {
        const response = await this.simulateApiCall("/auth/get-all-billings", {
          method: "GET",
          params: { clientId: clientId },
        });
        console.log("Client invoices loaded:", response);
      } catch (error) {
        console.error("Error loading invoices:", error);
      }
    },

    calculateManualDuration() {
      if (!this.manualStartTime || !this.manualEndTime) {
        this.manualDuration = 0;
        return;
      }

      const start = new Date("2000-01-01 " + this.manualStartTime);
      const end = new Date("2000-01-01 " + this.manualEndTime);

      if (end <= start) {
        this.manualDuration = 0;
        return;
      }

      const diffMs = end - start;
      const diffMinutes = Math.floor(diffMs / 60000);
      this.manualDuration = diffMinutes;
    },

    async addManualTime() {
      const client = this.clients.find((c) => c.id === this.selectedClient);
      const service = this.services.find((s) => s.id === this.selectedService);
      const invoice = this.invoiceOptions.find(
        (i) => i.id === this.selectedInvoice
      );

      const duration = this.manualDuration;
      const increment = this.getTimeIncrement();
      const billableMinutes = Math.ceil(duration / increment) * increment;
      const billableTime =
        this.getCurrentUnit === "hour"
          ? (billableMinutes / 60).toFixed(2)
          : billableMinutes.toFixed(0);

      const rate = parseFloat(this.getCurrentRate);
      const amount =
        this.getCurrentUnit === "hour"
          ? (rate * parseFloat(billableTime)).toFixed(2)
          : rate.toFixed(2);

      const billingEntry = {
        id: Date.now(),
        date: new Date(this.manualDate).toLocaleDateString(),
        client: client.name,
        clientInitials: client.initials,
        service: service.name,
        duration: `${Math.floor(duration / 60)}h ${duration % 60}m`,
        billableTime: billableTime,
        rate: rate,
        rateType: this.rateType,
        amount: amount,
        invoice: invoice.id === "new" ? "INV-" + Date.now() : invoice.label,
        invoiceStatus: "Pending",
        notes: this.sessionNotes,
        timestamp: new Date().toISOString(),
        manual: true,
      };

      this.billingHistory.unshift(billingEntry);

      // Reset manual entry form
      this.manualStartTime = "";
      this.manualEndTime = "";
      this.manualDuration = 0;
      this.sessionNotes = "";

      try {
        const response = await this.simulateApiCall("/auth/save-billing", {
          method: "POST",
          body: billingEntry,
        });
        console.log("Manual billing saved:", response);
      } catch (error) {
        console.error("Error saving manual billing:", error);
      }

      this.showSnackbar("Manual time entry added successfully!", "success");
    },

    viewBillingDetail(item) {
      this.showSnackbar(`Viewing details for ${item.service}`, "info");
      console.log("Billing details:", item);
    },

    deleteBilling(item) {
      const index = this.billingHistory.findIndex((b) => b.id === item.id);
      if (index > -1) {
        this.billingHistory.splice(index, 1);
        this.showSnackbar("Billing entry deleted", "error");
      }
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    simulateApiCall(endpoint, options) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            message: `API call to ${endpoint} successful`,
            data: options.body || {},
          });
        }, 500);
      });
    },
  },

  mounted() {
    // Load initial billing history
    this.billingHistory = [
      {
        id: 1,
        date: "12/15/2024",
        client: "John Smith",
        clientInitials: "JS",
        service: "Legal Consultation",
        duration: "2h 30m",
        amount: "875.00",
        invoice: "INV-001",
        invoiceStatus: "Pending",
      },
      {
        id: 2,
        date: "12/14/2024",
        client: "Sarah Johnson",
        clientInitials: "SJ",
        service: "Medical Consultation",
        duration: "1h 15m",
        amount: "250.00",
        invoice: "INV-002",
        invoiceStatus: "Paid",
      },
      {
        id: 3,
        date: "12/13/2024",
        client: "Michael Brown",
        clientInitials: "MB",
        service: "Document Review",
        duration: "3h 0m",
        amount: "750.00",
        invoice: "INV-003",
        invoiceStatus: "Pending",
      },
    ];
  },

  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
};
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}

.v-data-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-expansion-panel::before {
  box-shadow: none;
}
</style>
